import { useEffect, useRef, useState } from 'react'

/**
 * Custom hook to detect and handle loading transitions to prevent "Not Used" label flashing
 *
 * @param isLoading - Current loading state
 * @param contentLength - Current number of content items (e.g., transformedNodes.length)
 * @param maxTransitionTime - Maximum time to wait for content before showing "Not Used" (default: 5000ms)
 * @returns Whether to show loading state to prevent "Not Used" flash
 */
export const useLoadingTransition = (
    isLoading: boolean,
    contentLength: number,
    maxTransitionTime: number = 5000
): boolean => {
    // Track previous state
    const prevContentLength = useRef(0)
    const prevLoading = useRef(false)
    const timeoutId = useRef<NodeJS.Timeout | null>(null)

    // Detect if we're in a transition state
    const [isInTransition, setIsInTransition] = useState(false)
    
    useEffect(() => {
        // Clear any existing timeout
        if (timeoutId.current) {
            clearTimeout(timeoutId.current)
            timeoutId.current = null
        }

        const wasLoadingOrHadContent =
            prevLoading.current || prevContentLength.current > 0
        const nowEmpty = contentLength === 0
        const shouldTransition = wasLoadingOrHadContent && nowEmpty && !isLoading

        if (shouldTransition) {
            setIsInTransition(true)
            
            // Set timeout to end transition
            timeoutId.current = setTimeout(() => {
                setIsInTransition(false)
                timeoutId.current = null
            }, maxTransitionTime)
        } else if (!shouldTransition) {
            // End transition immediately if conditions no longer met
            setIsInTransition(false)
        }
        
        // Update previous state refs for next render
        prevContentLength.current = contentLength
        prevLoading.current = Boolean(isLoading)
        
        // Cleanup on unmount
        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current)
                timeoutId.current = null
            }
        }
    }, [contentLength, isLoading, maxTransitionTime])

    // Return whether to show loading state
    return Boolean(isLoading) || isInTransition
}
